/**
 * V2.0.80 — Wall-add menu state + handlers extracted from
 * FloorPlan2D.jsx.
 *
 * Owns the `wallAddMenu` popover state that opens when the user
 * clicks on a wall (via the parent's `handleSvgClick`). The parent
 * still detects the click and calls `open(config)` — this hook owns
 * everything that happens after:
 *
 *   - `handleAddPick({ kind, type })` — inserts a new door or window
 *     into `floor.doors[]` / `floor.windows[]`, then hands off to the
 *     `useOpeningDragMode` hook (via `placeDrag.beginDrag`) so the
 *     user can position it along the wall before the next click
 *     commits.
 *   - `handleMerge()` — collapses an interior wall by appending the
 *     opposite room's id to `merged_with` on the first room. Result:
 *     the two spaces read as one open-plan zone in every downstream
 *     renderer.
 *   - `close()` — closes the menu (also called after any pick).
 *
 * The menu component itself (`<WallAddMenu>`) is still owned by the
 * parent JSX — this hook is just the state + logic bag.
 */
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { emitInteractionEvent } from "../../lib/interactionTelemetry";

export default function useWallAddMenu({
  blueprint,
  floorIndex,
  pushHistory,
  onUpdateBlueprint,
  placeDrag, // returned by useOpeningDragMode — needed to enter drag mode after insert
}) {
  const [menu, setMenu] = useState(null);

  const open = useCallback((config) => {
    setMenu(config);
    emitInteractionEvent({
      tool: "wall_add_menu",
      action: "open",
      meta: { wall_kind: config?.wall?.kind || null },
    });
  }, []);

  const close = useCallback(() => {
    setMenu(null);
    emitInteractionEvent({
      tool: "wall_add_menu",
      action: "close",
      committed: false,
    });
  }, []);

  const handleMerge = useCallback(() => {
    if (!menu || !blueprint) {
      setMenu(null);
      return;
    }
    const wall = menu.wall;
    if (wall.kind !== "interior" || !wall.room_a || !wall.room_b) {
      setMenu(null);
      return;
    }
    pushHistory?.();
    const next = JSON.parse(JSON.stringify(blueprint));
    const f = (next.floors || [])[floorIndex - 1];
    if (!f || !Array.isArray(f.rooms)) {
      setMenu(null);
      return;
    }
    const ra = f.rooms.find((r) => r.id === wall.room_a);
    if (!ra) {
      setMenu(null);
      return;
    }
    const existing = Array.isArray(ra.merged_with) ? ra.merged_with : [];
    if (!existing.includes(wall.room_b)) {
      ra.merged_with = [...existing, wall.room_b];
    }
    onUpdateBlueprint?.(next);
    setMenu(null);
    const nameA = menu.mergeOption?.roomAName || "Room";
    const nameB = menu.mergeOption?.roomBName || "Room";
    toast.success(`Merged · ${nameA} + ${nameB} now flow as one open plan`);
    emitInteractionEvent({
      tool: "wall_add_menu",
      action: "merge_rooms",
      committed: true,
    });
  }, [menu, blueprint, floorIndex, pushHistory, onUpdateBlueprint]);

  const handleAddPick = useCallback(
    ({ kind, type }) => {
      if (!menu || !blueprint) {
        setMenu(null);
        return;
      }
      const wall = menu.wall;
      // V2.0.7 — Clamp position away from wall ends so a click exactly at
      // the edge doesn't produce a 0 / 1 position (which would render the
      // opening clipped against the adjacent wall).
      const clampedPos = Math.max(0.08, Math.min(0.92, wall.position ?? 0.5));
      pushHistory?.();
      const next = JSON.parse(JSON.stringify(blueprint));
      const f = (next.floors || [])[floorIndex - 1];
      if (!f) {
        setMenu(null);
        return;
      }
      if (!Array.isArray(f.doors)) f.doors = [];
      if (!Array.isArray(f.windows)) f.windows = [];
      const stamp = Date.now().toString(36);
      if (kind === "window") {
        f.windows.push({
          id: `W-${stamp}`,
          room: wall.room_id,
          side: wall.side,
          position: clampedPos,
          type,
          width: 1.2,
        });
      } else if (wall.kind === "exterior") {
        f.doors.push({
          id: `D-${stamp}`,
          room_a: wall.room_id,
          room_b: null,
          is_exterior: true,
          side: wall.side,
          position: clampedPos,
          type,
          width: type === "french" || type === "sliding_glass" ? 1.8 : 0.9,
          height: 2.1,
        });
      } else {
        f.doors.push({
          id: `D-${stamp}`,
          room_a: wall.room_a,
          room_b: wall.room_b,
          position: clampedPos,
          type,
          width: 0.9,
          height: 2.1,
        });
      }
      onUpdateBlueprint?.(next);
      setMenu(null);
      // V2.0.8 — enter place-by-drag mode so the user can slide the new
      // opening along the wall before committing.
      const insertedIdx = (kind === "window" ? f.windows : f.doors).length - 1;
      placeDrag?.beginDrag({
        kind,
        idx: insertedIdx,
        axis: wall.axis,
        coord: wall.coord,
        span_lo: wall.span_lo,
        span_hi: wall.span_hi,
        room_id: wall.kind === "exterior" ? wall.room_id : null,
        room_a: wall.kind === "interior" ? wall.room_a : null,
        room_b: wall.kind === "interior" ? wall.room_b : null,
        is_exterior: wall.kind === "exterior",
      });
      toast.success(`${kind === "window" ? "Window" : "Door"} added · drag to position`);
      emitInteractionEvent({
        tool: "wall_add_menu",
        action: kind === "window" ? "add_window" : "add_door",
        committed: true,
        meta: { type, wall_kind: wall.kind },
      });
    },
    [menu, blueprint, floorIndex, pushHistory, onUpdateBlueprint, placeDrag],
  );

  return {
    menu,
    open,
    close,
    handleMerge,
    handleAddPick,
  };
}
